import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { X, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import adminService from "../../api/services/admin.service";
import ExtraEmailsField, { cleanExtraEmails } from "../ExtraEmailsField";

const FIELDS = [
  { name: "first_name", label: "First Name", type: "text" },
  { name: "last_name", label: "Last Name", type: "text" },
  { name: "email", label: "Email", type: "email", wide: true },
  { name: "phone", label: "Phone", type: "tel" },
  { name: "address", label: "Address", type: "text", wide: true },
  { name: "city", label: "City", type: "text" },
  { name: "state", label: "State", type: "text" },
  { name: "zip_code", label: "ZIP Code", type: "text" },
];

const inputClass =
  "mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg font-manrope text-sm focus:outline-none focus:ring-2 focus:ring-[#F3BC48]";

const formFrom = (parent) => ({
  ...Object.fromEntries(FIELDS.map((f) => [f.name, parent?.[f.name] || ""])),
  additional_emails: parent?.additional_emails || [],
});

/**
 * Staff edit a parent's own details and extra contact emails.
 *
 * PUT /users/{id} writes every key it is sent and treats null as "clear",
 * so an empty field is left out of the payload rather than posted.
 */
export default function AdminParentForm({ isOpen, parent, onClose, onSaved }) {
  const [form, setForm] = useState(() => formFrom(parent));
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) setForm(formFrom(parent));
  }, [isOpen, parent]);

  if (!isOpen || !parent) return null;

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {};
    for (const { name } of FIELDS) {
      const value = form[name].trim();
      if (value) payload[name] = value;
    }
    payload.additional_emails = cleanExtraEmails(form.additional_emails);

    setIsSubmitting(true);
    try {
      const updated = await adminService.updateClient(parent.id, payload);
      toast.success("Parent details saved");
      onSaved?.(updated);
    } catch (error) {
      console.error("Failed to update parent:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to save parent details",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold font-kollektif text-text-primary">
            Edit Parent
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Close"
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {FIELDS.map((field) => (
              <div key={field.name} className={field.wide ? "sm:col-span-2" : ""}>
                <label
                  htmlFor={`parent-${field.name}`}
                  className="text-sm font-medium text-gray-700 font-manrope"
                >
                  {field.label}
                </label>
                <input
                  id={`parent-${field.name}`}
                  type={field.type}
                  name={field.name}
                  value={form[field.name]}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={inputClass}
                />
              </div>
            ))}

            <div className="sm:col-span-2">
              <p className="text-sm font-medium text-gray-700 font-manrope">
                Extra contact emails
              </p>
              <p className="text-xs text-text-muted font-manrope mb-2">
                Copied on every club email to this family (not password emails).
              </p>
              <ExtraEmailsField
                value={form.additional_emails}
                onChange={(emails) =>
                  setForm((prev) => ({ ...prev, additional_emails: emails }))
                }
                disabled={isSubmitting}
                inputClassName={`${inputClass} mt-0`}
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-manrope"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-semibold text-[#173151] bg-[#F3BC48] hover:bg-[#e5a920] rounded-lg transition-colors font-manrope disabled:opacity-50 flex items-center gap-2"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Save Changes
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
